import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTokensContext } from "./TokensContextProvider";
import { useOperatorContext } from "../comps/login/OperatorContextProvider";
import { authGet } from "../infra/AuthFetcher";
import { configsUrl } from "./EnvContextProvider";
import useSmartRef from "../hooks/useSmartRef";
import { getSetupCard, getSetupCardItemsPath, OrderedSteps } from "../hooks/useSelector";
import { getFolderName, getFolderNode } from "../hooks/useConfigsNavigator";
import { getParentPath } from "./ConfigPathContextProvider";

//               Context
// ----------------------------------------
const ConfigsDataContext = createContext();

//
// ------------  STATIC  ----------------------------------------------------------------------------- //
//
      export const K_INFO = 'kiponosInfo'            // TODO: RENAME to special "k" unicode   
      export const K_VALUE = 'value';                // TODO: RENAME to special "v" unicode
      export const K_RootPrefix = '$.rootAccount';

      export const jp = require('jsonpath');

      const ConfigFetchStatus = {
        Fetched:  {title: 'Fetched',  icon: '🟢'},
        Unloaded: {title: 'Unloaded', icon: '🟤'},
        Loading:  {title: 'Loading...',icon:'🟣'},
        Error:    {title: 'Error',    icon: '🔴'},
        Timeout:  {title: 'Timeout',  icon: '🏁'}
      }

      export const LoadCycle = {
        Unloaded: 'Configs-Unloaded',
        Loading: 'Configs-Loading...',
        Fetched: 'Configs-Fetched',
        Error:  'Configs-Error'
      }      

      export const isMetaData = (key) => {
        return key === K_INFO;
      }

      export const isItem = (keyNode) => {
        return keyNode?.hasOwnProperty(K_VALUE);
      }

      export const isFolder = (keyNode) => {
        return !isItem(keyNode);
      }

      // ---[  Get Node  ]---
      const getNodeByTreeAndPath = (tree, path) => {
        if (tree && path) {    
          return jp.query(tree, path)[0];
        }
        else {
          return null;
        }
      }


      // // Get Parent Path
      // // --- folder.sub1['sub2'].sub3
      // // --- ['folder']['sub1'].sub2['sub3']
      // const getParentNode = (tree, path) => {
      //   if (tree && path) {
      //     return jp.parent(tree, path);
      //   }
      //   else {
      //     return null;
      //   }
      // }

      const initFolder = (node, folderName, modifiedInfo) => {
        node[folderName] = {
          [K_INFO]: {
            whenModified: modifiedInfo.whenModified ? modifiedInfo.whenModified : Date.now(),
            whoModified: modifiedInfo.whoModified,
            whyModified: modifiedInfo.whyModified ? modifiedInfo.whyModified : 'webInit'
          }
        }

        if (node[K_INFO].folderNameInProgress === folderName) {
          delete node[K_INFO].folderNameInProgress;
        }

        return node[folderName];
      }


//               ConfigsDataContextProvider
// --------------------------------------------------------------------------------------------------- //
//
export const ConfigsDataContextProvider = ({children}) => {
  const fetchStatusRef                            = useRef(ConfigFetchStatus.Unloaded);  
  const {get:getConfigsTree, set:setConfigsTree}  = useSmartRef({});
  const gateKeeperRef                             = useRef(Promise.resolve(Date.now()));
  const fetchPromiseRef                           = useRef(null);
  const {idToken, accessToken}                    = useTokensContext();
  const [loadCycle, setLoadCycle]                 = useState(LoadCycle.Unloaded);
  const [pageReady, setPageReady]                 = useState(false);
  const [configReloadMsg, setConfigReloadMsg] = useState(null); // {msg:'', params: ''}  E.g: {msg:'Warning: Suspicious activity! auto-logout in', params: '5'}  
  const [reasoning, setReasoning] = useState(false);
  const [insightRejected, setInsightRejected] = useState(null);


  // TODO: Refactor operatorContext!!!  Don't use reduce Dispatcher! use only simple useState.
  const {operatorState, isOperatorFlag, OperatorFlag} = useOperatorContext();

  // ---------[  updateConfigsRefSafely  ]-----------
  const updateConfigsRefSafely = useCallback((nextConfigsTree) => {
    const nextPromise = new Promise( (resolve, reject) => {
      gateKeeperRef.current.then( ()=>{
        setConfigsTree(nextConfigsTree);
        resolve(Date.now());
      })
      .catch(reject);
    });

    gateKeeperRef.current = nextPromise;
    return gateKeeperRef.current;  
  }, [setConfigsTree]);

  // -------[  ref IsUnloaded  ]--------
  const refIsUnloaded = useCallback(() => {
    return !Boolean(fetchStatusRef.current) || Boolean(fetchStatusRef.current === ConfigFetchStatus.Unloaded);
  }, []);

  // -------[   ref SetFetched   ]------
  const refSetFetched = useCallback(() => {
    fetchStatusRef.current = ConfigFetchStatus.Fetched;
  }, []);
     
  const isConfigsUnloaded = useCallback( ()=> loadCycle === LoadCycle.Unloaded, [loadCycle]);  
  const setConfigsLoading = () => setLoadCycle(LoadCycle.Loading);
  const setConfigsFetched = () => setLoadCycle(LoadCycle.Fetched);
  const setConfigsFetchError = () => setLoadCycle(LoadCycle.Error);
  //
  //------[  INIT And FETCH  ]------
  //
  const initAndFetch = useCallback(()=>{

    const refSetLoading = () => {
      fetchStatusRef.current = ConfigFetchStatus.Loading;
    }
  
    const refSetFetchedError = () => {
      fetchStatusRef.current = ConfigFetchStatus.Error;
    }

    const isConfigsFetched = () => loadCycle === LoadCycle.Fetched;    
  

    if (!refIsUnloaded()) {
      console.log('Configs Fetcher: local ref not set to unloaded. Fetch Ignored.')
      return;
    }

    if (!isConfigsUnloaded()) {
      console.debug('Configs Fetcher: Reducer: Configs Not in Unloaded state. Fetch Ignored');
      return;
    }

    // Defender
    if (!idToken) {
      return;
    }

    // TODO: Refresh the access token using this one.  So in this case, id+access --> get new id+access...
    if (!accessToken) {
      return;
    }

    if (isConfigsFetched()) {
      return;
    }

    if (!isOperatorFlag(OperatorFlag.Ready)) {
      return;
    }

    //
    // Definition Only ---  Fetch Only Once ---    
    // 
    const fetchOnlyOnce = async () => {
      setConfigsLoading();
      refSetLoading();
      //console.log('[ConfigsDataContextProvider fetchOnlyOnce] ---[ Started ]---');
      
      const fetchPromise = new Promise( (resolve, reject) => {

        const fetchTimeout = setTimeout(()=>{          
          // TODO: 1. Test that Disconnect occurs gracefully. 
          //       2. Set another timeout to Retry or simply eject the operator?
          // disconnect(); 
          // refSetError();
          refSetFetchedError();
          setConfigsFetchError();
          //        <<<  REJECT  by Timeout  >>>
          reject("Configs Fetch Timeout");
        }, 5000);
        
        console.log('[ConfigsDataContextProvider fetchOnlyOnce] Configs LOADING NOW...');
        authGet(idToken, configsUrl)
        .then(allConfigsResponse => {
          refSetFetched();
          // SET DATA 
          updateConfigsRefSafely(allConfigsResponse)
            .then(()=> resolve('AlreadyFetched'))
            .then(setConfigsFetched)
            .then(()=>console.log('[ConfigsDataContextProvider] Configs FETCHED]'));
          //setConfigsFetched();
        })
        .catch( fetchFailure => {          
          // Retry Here?
          console.error('[ConfigsDataContextProvider fetchOnlyOnce authGet] Failed: %s', fetchFailure);
          refSetFetchedError();
          setConfigsFetchError()
          reject(fetchFailure);
        })
        .finally( ()=> {          
          clearTimeout(fetchTimeout);
          //console.log('[ConfigsDataContextProvider fetchOnlyOnce authGet] End.');
        });
      })
      .then( fetchedData => {
        // Life goes on.
        //console.log('[ConfigsDataContextProvider fetchOnlyOnce] [ Promise End OK. Fetched %s]', fetchedData.length);
      })       
      .catch( runOnlyOnceEngineFailedReason => {
        // Already marked the Promise to allow another attempt.
        console.error('[ConfigsDataContextProvider fetchOnlyOnce] Promise ERROR [reason: %s]', runOnlyOnceEngineFailedReason);
      })
      .finally (()=>{
        fetchPromiseRef.current = null;
      });

      // ---  Return Promise  ---
      return fetchPromise;
    }

    // PROMISE LOCK
    fetchPromiseRef.current = fetchOnlyOnce();

  }, [  OperatorFlag.Ready,
        loadCycle,
        idToken, 
        accessToken, 
        isOperatorFlag, 
        updateConfigsRefSafely,
        isConfigsUnloaded, 
        refIsUnloaded, 
        refSetFetched, 
        ]);


  //
  //---  Effect OperatorReady ---
  //
  useEffect(()=>{
    const refIsFetched = () => Boolean(fetchStatusRef.current) && Boolean(fetchStatusRef.current === ConfigFetchStatus.Fetched);    
  
    if (!refIsFetched() && isOperatorFlag(OperatorFlag.Ready) && isConfigsUnloaded() && !fetchPromiseRef.current && refIsUnloaded()) {
      initAndFetch();
    }
  }, [operatorState, initAndFetch, isOperatorFlag, OperatorFlag.Ready, isConfigsUnloaded, refIsUnloaded]);


  // //------[  Get Node by Path  ]------
  // //
  // const getNodeByPath = useCallback((path) => {
  //   return getNodeByTreeAndPath(getConfigsTree(), path);
  // }, [getConfigsTree]);


  //------[  Get Node  ]------
  //
  // const getNode = useCallback( (aPath) => getNodeByPath(aPath), [getNodeByPath]);
  const getNode = useCallback( (aPath) => getNodeByTreeAndPath(getConfigsTree(), aPath), [getConfigsTree]);
    

  // ------[   Add BlankFolder   ]------
  //
  const addBlankFolder = useCallback( async (parentPath) => {
    // console.log('[ConfigsDataContextProvider addBlankFolder] [parentPath: %s]', parentPath);
    
    const promise = new Promise( (resolve, reject) => {
      const copyTree = {...getConfigsTree()};
      const copyParentFolder = getNodeByTreeAndPath(copyTree, parentPath);
      copyParentFolder[''] = {kiponosInfo:{isRenameMode:true, isNew: true}};
      updateConfigsRefSafely(copyTree).then(()=> resolve('BlankFolderAdded'));
    });
    
    return promise;
  }, [getConfigsTree, updateConfigsRefSafely]);


  //------[  Add Setup BlankFolder  ]------
  //
  const addSetupBlankFolder = useCallback( async (steps, stepName) => {
    return new Promise((resolve, reject) => {
      const myCard = getSetupCard(stepName);
      const cardMainPath = myCard.getPath(steps);  // The Card's Path is where the Previous Card Selection Ends! So "rels" path is $.[.....][apps][a]
      const myItemsPath = getSetupCardItemsPath(steps, stepName);
      
      const copyTree = {...getConfigsTree()};
      
      const myFolder = getNodeByTreeAndPath(copyTree, myItemsPath); // path after the stepName. E.g: on "rels" card: $.[...][rels]
      const cardMainNode = getNodeByTreeAndPath(copyTree, cardMainPath);
      
      if (!myFolder) { // E.g: "rels" itself not created yet...
        cardMainNode[stepName] = {[K_INFO]: {}}; // The server will detect this parent doesn't exist yet and will create it as well.
      }

      cardMainNode[stepName][''] = {[K_INFO]:{isRenameMode:true, isNew:true}}; // Our SubFolder item.  E.g: Will create [rels] = {k: {}}

      updateConfigsRefSafely(copyTree).then(()=> resolve(cardMainPath));
    });
  }, [getConfigsTree, updateConfigsRefSafely]);


  //------[  Delete Local Folder  ]------
  // 
  const deleteFolder = useCallback( async (folderDeleted) => {
    return new Promise( (resolve, reject) => {
      // console.log('[ConfigsDataContextProvider deleteFolder] [folderDeleted: %s]', JSON.stringify(folderDeleted, null, 2));
      
      const copyTree = {...getConfigsTree()};
      const copyParentFolder = getNodeByTreeAndPath(copyTree, folderDeleted.basePath);
      const treeToDelete = copyParentFolder[folderDeleted.folderName];

      // If exists, delete.
      if (treeToDelete) { 
        delete copyParentFolder[folderDeleted.folderName];
        updateConfigsRefSafely(copyTree)
          .then(()=> resolve('Deleted'));
      }
      else {
        console.warn('[ConfigPathContextProvider deleteFolder] Not Exists. Already Deleted? [folderToDelete: %s]', folderDeleted.folderName);
        resolve('FolderNotExists');
      }      
    });
  }, [getConfigsTree, updateConfigsRefSafely]);


  // ------[  Delete Blank Folder  ]------
  //
  const deleteBlankFolder = useCallback( async (parentPath) => {
    return new Promise( (resolve, reject) => {
      const copyTree = {...getConfigsTree()};
      const copyParentFolder = getNodeByTreeAndPath(copyTree, parentPath);
      delete copyParentFolder[''];
      updateConfigsRefSafely(copyTree).then(()=> resolve('BlankFolderDeleted'));
    });    
  }, [getConfigsTree, updateConfigsRefSafely]);


  // ------[  Undo Rename Folder  ]------
  //
  const setRenameMode = useCallback( async (parentPath, origName, isRename = true) => {
    const promise = new Promise( (resolve, reject) => {
      const copyTree = {...getConfigsTree()};
      const copyParentFolder = getNodeByTreeAndPath(copyTree, parentPath);

      if (isRename) {
        copyParentFolder[origName][K_INFO].isRenameMode = true;
      }
      else {
        delete copyParentFolder[origName][K_INFO].isRenameMode;
        delete copyParentFolder[origName][K_INFO].folderCreatedErrorInfo;
        delete copyParentFolder[origName][K_INFO].isNew;
        delete copyParentFolder[origName][K_INFO].inProgress;
      }
      
      updateConfigsRefSafely(copyTree).then(()=> resolve('RenameMode'));
    });
    
    return promise;
  }, [getConfigsTree, updateConfigsRefSafely]);


  // const updateKInfo = useCallback(async (fullPath, kInfo) => {

  // }, []);


  //@Deprecated to be removed - <CreateSubFolder /> uses it's new folderNameInProgress so SubFolder will only use rename.
  const setCreatingInProgress = useCallback( async (parentPath, folderName, isInProgress = true) => {
    return new Promise( (resolve, reject) => {
      const copyTree = {...getConfigsTree()};
      const copyParentFolder = getNodeByTreeAndPath(copyTree, parentPath);
      if (isInProgress) {
        // Rename the blank folder to the new folderName
        copyParentFolder[folderName] = {...copyParentFolder['']};
        delete copyParentFolder[''];
        copyParentFolder[folderName][K_INFO] = {...copyParentFolder[folderName][K_INFO], inProgress: true};
      }
      else {
        delete copyParentFolder[folderName][K_INFO].inProgress;
        delete copyParentFolder[folderName][K_INFO].isNew;
        delete copyParentFolder[folderName][K_INFO].isRenameMode;
      }
      updateConfigsRefSafely(copyTree).then(()=> resolve(`InProgress ${isInProgress}`));
    });
  }, [getConfigsTree, updateConfigsRefSafely]);


  const setCreateKeyInProgress = useCallback(async (folderPath, keyName, isInProgress = true) => {
    return new Promise((resolve, reject) => {
      const copyTree = {...getConfigsTree()};
      const copyFolder = getNodeByTreeAndPath(copyTree, folderPath);
      if (isInProgress) {        
        copyFolder[K_INFO] = {...copyFolder[K_INFO], keyNameInProgress: keyName};
      }
      else {
        delete copyFolder[K_INFO].keyNameInProgress;
      }

      updateConfigsRefSafely(copyTree).then(()=> resolve(`InProgress ${isInProgress}`));
    });
  }, [getConfigsTree, updateConfigsRefSafely]);

  const tagCreateFolderInProgress = useCallback(async (parentFolderPath, folderName, turnOn) => {

    const createParent = (data) => {
      const stepName = getFolderName(parentFolderPath);
      const cardPath = getParentPath(parentFolderPath); // 
      const folder = getFolderNode(data, cardPath);
      folder[stepName] = {[K_INFO]: {}};
      return folder;
    }

    return new Promise((resolve, reject) => {
      const copyTree = {...getConfigsTree()};
      const parentFolderCheck = getNodeByTreeAndPath(copyTree, parentFolderPath);

      // For Wizard, parent might not exists yet. (i.e. like "rels" parent for a new release folder name)
      const parentFolder = parentFolderCheck ? parentFolderCheck : createParent(copyTree);

      if (turnOn) {
        parentFolder[K_INFO].folderNameInProgress = folderName;
      }
      else {
        delete parentFolder[K_INFO].folderNameInProgress;
      }

      updateConfigsRefSafely(copyTree)
        .then(()=>resolve(`inProgress ${turnOn ? 'ON' : 'OFF'}`));
    });
  }, [getConfigsTree, updateConfigsRefSafely]);

  const setFolderEdit = useCallback(async (parentFolderPath, folderName, isOn) => {
    return new Promise((resolve, reject) => {
      const copyTree = {...getConfigsTree()};
      const parentFolder = getNodeByTreeAndPath(copyTree, parentFolderPath);

      if (isOn) {
        parentFolder[folderName][K_INFO].folderEdit = true;
      }
      else {
        delete parentFolder[folderName][K_INFO].folderEdit;
      }

      updateConfigsRefSafely(copyTree)
        .then(()=>resolve(`tagFolderEdit ${isOn?'On':'Off'}`));
    });
  }, [getConfigsTree, updateConfigsRefSafely]);


  // $.rootAccount['apps']['Medical Server']['rels']['3.2i']['envs']['Staging']['cfgs']['Clinic Settings']
  // {
  //    "basePath":"$.rootAccount['apps']['Local Test App']['rels']['1.0']['envs']['Prod']['kiponosInfo']",
  //            
  //    "selectedTeamId":"1da1937f-e52e-4830-b690-685702775623",
  //    "sdkIdToken":"eyJ...",
  //    "sdkAccessToken":"eyJ..."
  // }
  //------[  Update SDK Data  ]------
  //
  const updateSdkData = useCallback( async (anSdkGenereatedData)=>{
    return new Promise((resolve, reject) => {
      const {basePath, sdkIdToken, sdkAccessToken} = anSdkGenereatedData;
      const copyTree = {...getConfigsTree()};
      const envNode   = getNodeByTreeAndPath(copyTree, basePath);
      if (envNode) {
        envNode.sdk = {};
        envNode.sdk.basePath        = basePath;
        envNode.sdk.sdkIdToken      = sdkIdToken;
        envNode.sdk.sdkAccessToken  = sdkAccessToken;
      }

      updateConfigsRefSafely(copyTree)
        .then(()=>resolve('sdkDataUpdated'));
    })
  }, [getConfigsTree, updateConfigsRefSafely]);


  //------[  Create Folder  ]------
  //
  const createFolder = useCallback( async (folderCreated) => {
    return new Promise( (resolve, reject) => {
      const copyTree = {...getConfigsTree()};
      const copyParentFolder = getNodeByTreeAndPath(copyTree, folderCreated.path);
      
      // First SubFolder of a new SetupCard.  E.g: First ['rels']['ver 3.8.2'].. so 'rels' was also created on the server and needs to be created locally here as well.
      if (!copyParentFolder) {
            // check if parent is a valid stepName.
            const stepNameToCreate = getFolderName(folderCreated.path);            
            if (OrderedSteps.includes(stepNameToCreate)) {
                // Start with the parent of the card
                const cardParentPath = getParentPath(folderCreated.path);
                const cardParentNode = getNodeByTreeAndPath(copyTree, cardParentPath);
                
                // Init Parent
                const cardNode = initFolder(cardParentNode, stepNameToCreate, folderCreated);

                // Init Folder
                initFolder(cardNode, folderCreated.folderName, folderCreated);
            }
            else {
                // ERROR: Not Expecting a missing parent folder other then in a new SetupCard
                console.error('[ConfigsDataContextProvider createFolder] Parent Node missing and is not a Card Name');
                resolve('noop');
            }
      }
      else { // Valid copyParentFolder
        initFolder(copyParentFolder, folderCreated.folderName, folderCreated);
      }

      updateConfigsRefSafely(copyTree).then(()=> {
        // console.log('[ConfigsDataContextProvider createFolder] Created: %s - %s', folderCreated.path, folderCreated.folderName);
        resolve('Created');
      });
    }).then( () => deleteBlankFolder(folderCreated.path) ); // TODO: Review/test - We delete the blank folder in ANY CASE, e.g: even in "noop" where the parent folder is missing.
  }, [getConfigsTree, updateConfigsRefSafely, deleteBlankFolder]);


      //----------[  Rename Local Folder  ]------------------------------------//
     // 1. Backup Old Tree.                                                   //
    //  2. Delete Old Folder                                                 //
   //   3. Create New Folder with Bakcup Old Tree                           //
  const renameFolder = useCallback( async (folderRenamed) => {
    return new Promise((resolve, reject) => {
      const copyTree = {...getConfigsTree()};
      const copyParentFolder = getNodeByTreeAndPath(copyTree, folderRenamed.basePath);

      // If Old Exist, "move" it to the new
      const oldTree = copyParentFolder[folderRenamed.oldFolderName];
      if (oldTree) {
        delete copyParentFolder[folderRenamed.oldFolderName];
        copyParentFolder[folderRenamed.newFolderName] = oldTree;
        copyParentFolder[folderRenamed.newFolderName][K_INFO] = {...copyParentFolder[folderRenamed.newFolderName][K_INFO],
          whenModified: folderRenamed.whenModified,
          whoModified: folderRenamed.whoModified,
          whyModified: folderRenamed.whyModified
        };

        delete copyParentFolder[folderRenamed.newFolderName][K_INFO].isNew;
        delete copyParentFolder[folderRenamed.newFolderName][K_INFO].isRenameMode;
        delete copyParentFolder[folderRenamed.newFolderName][K_INFO].folderRenamedErrorInfo;
        delete copyParentFolder[folderRenamed.newFolderName][K_INFO].inProgress;

        updateConfigsRefSafely(copyTree).then(()=> {
          console.log('[ConfigsDataContextProvider renameFolder] Renamed [old: %s] [new: %s]', folderRenamed.oldFolderName, folderRenamed.newFolderName);        
          resolve('Renamed')
        });
      }
      else {
        // TODO: Verify - Most likely already renamed. So Ignore?
        console.warn('[ConfigsDataContextProvider renameFolder] Old Not Found! Already Renamed? [old: %s] [new: %s]', folderRenamed.oldFolderName, folderRenamed.newFolderName);        
        resolve('noop');
      }
    });
  }, [getConfigsTree, updateConfigsRefSafely]);



  // ==============   ITEMS  LOCAL   ================= //


  // Local Create Item
  //
  // TODO: action - reloadConfig... re-implement
  //
  const createConfigItem = useCallback( async (configKeyCreated) => {
    return new Promise( (resolve, reject) => {
      const {basePath:folderPath, key:keyName, kiponosInfo:kInfo, action} = configKeyCreated;
      const copyTree = {...getConfigsTree()};
      const copyFolder = getNodeByTreeAndPath(copyTree, folderPath);
      if (copyFolder) {
        if (!copyFolder[keyName]) {
          copyFolder[keyName] = {};
          copyFolder[keyName][K_VALUE] = '';
          copyFolder[keyName][K_INFO] = {...kInfo, justCreated: true}
          updateConfigsRefSafely(copyTree)
            .then(() => setCreateKeyInProgress(folderPath, null, false))
            .then(() => {              
              console.log('ConfigsDataContextProvider createConfigItem] [configKeyCreated: %s]', JSON.stringify(configKeyCreated, null, 2));    
              resolve('ItemCreated');
            })
        }
        else {
          // Already Exists
          console.warn('[ConfigsDataContextProvider createConfigItem] Key Already Exists (and not createInProgress) - Just Created? [folderPath: %s] [keyName: %s]', folderPath, keyName);
          resolve('noop: KeyNameAlreadyExists')
        }
      }
      else {
        // Error, Item Created but its folder is gone! maybe just deleted?
        console.warn('[ConfigsDataContextProvider createConfigItem] Folder Not Found! [folderPath: %s] - Folder Deleted while key created? [keyName: %s]', folderPath, keyName);
        resolve('noop: FolderNotFound');
      }              
    });
  }, [getConfigsTree, updateConfigsRefSafely, setCreateKeyInProgress]);


  const clearJustUpdated = useCallback(async (folderPath, keyName) => {
    return new Promise((resolve, reject) => {
      const copyTree = {...getConfigsTree()};
      const folder   = getNodeByTreeAndPath(copyTree, folderPath);
      if (Boolean(folder) && Boolean(folder[keyName])) {
        setTimeout(()=>{
          delete folder[keyName][K_INFO].justCreated;
          delete folder[keyName][K_INFO].justRenamed;
          delete folder[keyName][K_INFO].justUpdated;
          updateConfigsRefSafely(copyTree)
            .then(()=>resolve('JustUpdatedRemoved'));
        }, 2341);
      }
      else {
        resolve('noop: Folder or KeyName not exist');
      }      
    })
  }, [getConfigsTree, updateConfigsRefSafely]);


  const renameConfigKey = useCallback(async (configKeyRenamed) => {
    return new Promise((resolve, reject) => {
      const {basePath:folderPath, oldKey, newKey, whenModified, whoModified, whyModified, action} = configKeyRenamed;
      const copyTree = {...getConfigsTree()};
      const folder = getNodeByTreeAndPath(copyTree, folderPath);
      if (folder) {
        if(folder[oldKey]) {
          // Create New
          folder[newKey] = {};
          folder[newKey] = {...folder[oldKey]};
          folder[newKey][K_INFO].whenModified = whenModified;
          folder[newKey][K_INFO].whoModified  = whoModified;          
          folder[newKey][K_INFO].whyModified  = whyModified;
          folder[newKey][K_INFO].justRenamed  = true;
          folder[newKey][K_INFO].keySuccess = true;

          // Remove fields not needed.
          delete folder[newKey][K_INFO].keyEdit;
          delete folder[newKey][K_INFO].keyRenamedErrorInfo;

          delete folder[oldKey];

          updateConfigsRefSafely(copyTree)            
            .then(()=>resolve('KeyRenamed'));
        }
        else {
          console.error('[ConfigsDataContextProvider renameConfigItem] oldKey Not Found [folderPath: %s] [oldKey: %s]',folderPath, oldKey);
          resolve('noop: oldKeyNotFound');
        }
      }
      else {
        console.error('[ConfigsDataContextProvider renameConfigItem] Folder Not Found [folderPath: %s] when rename key [from: %s] [to: %s]', folderPath, oldKey, newKey);
        resolve('noop: FolderNotFound');
      }
    });
  }, [getConfigsTree, updateConfigsRefSafely])

  //------[   Tag  keyCreatedError   ]------
  //
  const tagKeyCreatedError = useCallback( async (keyCreatedError) => {
    return new Promise((resolve, reject) => {
      console.log('ConfigsDataContextProvider tagKeyCreatedError] [keyCreatedError: %s]', JSON.stringify(keyCreatedError, null, 2));
      const {errorTag, basePath:folderPath} = keyCreatedError;
      if (errorTag === 'prop-prog-key') {
        const copyTree = {...getConfigsTree()};
        const copyFolder = getNodeByTreeAndPath(copyTree, folderPath);
        copyFolder[K_INFO].keyCreatedError = true; // TODO: Seems no need for this one anymore. Delete it. We're using onlu the xErrorInfo
        copyFolder[K_INFO].keyCreatedErrorInfo = keyCreatedError;
        updateConfigsRefSafely(copyTree)
          .then(() => resolve('taggedError'))
      }
      else {
        resolve(`noop: Unknown error tag: ${errorTag}`);
      }
    });
  }, [getConfigsTree, updateConfigsRefSafely]);


  //------[   Tag  FolderCreatedError   ]------
  //
  const tagFolderCreatedError = useCallback( async (folderCreatedError, isOn = true) => {
    return new Promise((resolve, reject) => {
      console.log('[ConfigsDataContextProvider tagFolderCreatedError] [data: %s]', JSON.stringify(folderCreatedError, null, 2));
      if (folderCreatedError) {
        const {basePath} = folderCreatedError;
        const copyTree = {...getConfigsTree()};
        const parentFolder = getNodeByTreeAndPath(copyTree, basePath);
        
        if (isOn) {          
          parentFolder[K_INFO].folderCreatedErrorInfo = folderCreatedError;
        }
        else {
          console.log('Untag FolderCreatedError [parentFolder: %s]', basePath);          
          delete parentFolder[K_INFO].folderCreatedErrorInfo;
          delete parentFolder[K_INFO].folderNameInProgress
        }
  
        updateConfigsRefSafely(copyTree)
          .then(()=>resolve('tagFolderCreateError'));
      }
      else {
        resolve('noop: No Arg')
      }
    });
  }, [getConfigsTree, updateConfigsRefSafely]);

  const tagFolderRenamedError = useCallback(async (folderRenamedError) => {
    return new Promise((resolve, reject) => {
      //console.log('tagFolderRenamedError: %s', JSON.stringify(folderRenamedError, null, 2));
      const {basePath, newKey:newFolderName, oldKey:oldFolderName} = folderRenamedError;
      const copyTree = {...getConfigsTree()};
      const parentFolder = getNodeByTreeAndPath(copyTree, basePath);
      parentFolder[oldFolderName][K_INFO].folderRenamedErrorInfo = folderRenamedError;
      updateConfigsRefSafely(copyTree)
        .then(()=>resolve('taggedError'));
      // deleteFolder({basePath:basePath, folderName:newFolderName})
      //   .then(()=>setRenameMode(basePath, oldFolderName, true))
      //   .then(()=>updateConfigsRefSafely(copyTree))
      //   .then(()=>resolve('taggedFolderRenamedError'));      
    });
  }, [getConfigsTree, updateConfigsRefSafely]);

  //------[   Reset  FolderCreatedError   ]------
  //
  // const resetFolderCreatedError = useCallback(async (folderCreatedError) => {
  //   return new Promise((resolve,reject) => {
  //     if (folderCreatedError) {
  //       console.log('[ConfigsDataContextProvider resetFolderCreatedError] [data: %s]', JSON.stringify(folderCreatedError, null, 2));
  //       const {basePath, newKey:folderName} = folderCreatedError;
  //       const copyTree = {...getConfigsTree()};
  //       const parentFolder = getNodeByTreeAndPath(copyTree, basePath);
        
  //       delete parentFolder[K_INFO].folderCreatedErrorInfo;
  //       updateConfigsRefSafely(copyTree)
  //         .then(()=>resolve('resetError'));
  //     }
  //     else {
  //       console.log('[ConfigsDataContextProvider resetFolderCreatedError] Ignoring - Missing arg');
  //       resolve('noop: Missing arg');
  //     }
  //   });
  // }, [getConfigsTree, updateConfigsRefSafely]);

  const resetFolderRenamedError = useCallback(async (folderRenamedError) => {
    return new Promise((resolve, reject) => {
      if(folderRenamedError) {
        const {basePath, newKey:folderName} = folderRenamedError;
        const copyTree = {...getConfigsTree()};
        const parentFolder = getNodeByTreeAndPath(copyTree, basePath);
        delete parentFolder[folderName][K_INFO].folderRenamedErrorInfo;
        updateConfigsRefSafely(copyTree)
          .then(()=>resolve('resetError'));
      }
      else {
        resolve('noop: Missing arg');
      }
    });
  },[getConfigsTree, updateConfigsRefSafely]);


  //------[   Tag  keyRenamedError   ]------
  //
  const tagKeyRenamedError = useCallback( async (keyRenamedError) => {
    return new Promise((resolve, reject) => {
      console.log('ConfigsDataContextProvider tagKeyRenamedError] [keyRenamedError: %s]', JSON.stringify(keyRenamedError, null, 2));
      const {errorTag, basePath:folderPath, oldKey} = keyRenamedError;
      if (errorTag === 'prop-prog-key') {
        const copyTree = {...getConfigsTree()};
        const folder = getNodeByTreeAndPath(copyTree, folderPath);        
        folder[oldKey][K_INFO].keyRenamedErrorInfo = keyRenamedError;
        updateConfigsRefSafely(copyTree)
          .then(() => resolve('taggedError'))
      }
      else {
        console.error('[ConfigsDataContextProvider tagKeyRenamedError] Unknown Error Tag: [errorTag: %s] [folderPath: %s] [oldKey: %s]', errorTag, folderPath, oldKey);        
        resolve(`noop: Unknown error tag: ${errorTag}`);
      }
    });
  }, [getConfigsTree, updateConfigsRefSafely]);



  //------[   Reset  keyCreatedError   ]------
  //
  const resetKeyCreatedError = useCallback( async (keyCreatedError) => {
    return new Promise((resolve, reject) => {
      if (keyCreatedError) {
        console.log('[ConfigsDataContextProvider resetKeyCreatedError] [keyCreatedError: %s]', JSON.stringify(keyCreatedError, null, 2));
        const {errorTag, basePath:folderPath} = keyCreatedError;
        if (errorTag === 'prop-prog-key') {
          const copyTree = {...getConfigsTree()};
          const copyFolder = getNodeByTreeAndPath(copyTree, folderPath);
          delete copyFolder[K_INFO].keyCreatedError;
          delete copyFolder[K_INFO].keyCreatedErrorInfo;
          updateConfigsRefSafely(copyTree)
            .then(()=>resolve('resetError'));
        }
      }
      else {
        console.log('[ConfigsDataContextProvider resetKeyCreatedError] Ignoring - No keyCreatedError to reset');
        resolve('noop: Arg not provided');
      }      
    });

  }, [getConfigsTree, updateConfigsRefSafely]);

  //------[   Tag ValueEditing   ]------
  //
  const setValueEditing = useCallback( async (folderPath, keyName, isEditing = true) => {
    return new Promise((resolve, reject) => {
      console.log('[ConfigPathContextProvider setValueEditing] [folderPath: %s] [keyName: %s] [isEditing: %s]',folderPath, keyName, isEditing);
      const copyTree = {...getConfigsTree()};
      const copyFolder = getNodeByTreeAndPath(copyTree, folderPath);
      if (isEditing) {
        copyFolder[keyName][K_INFO].valEdit = true; // To visualize Meta Flags        
      }
      else {
        delete copyFolder[keyName][K_INFO].valEdit;
      }
      updateConfigsRefSafely(copyTree)
        .then(()=> resolve('ValEdit'));
    });
  }, [getConfigsTree, updateConfigsRefSafely]);

  const setTitleEditing = useCallback( async (folderPath, keyName, isEditing = true) => {
    return new Promise((resolve, reject) => {
      console.log('[ConfigPathContextProvider setTitleEditing] [folderPath: %s] [keyName: %s] [isEditing: %s]',folderPath, keyName, isEditing);
      const copyTree = {...getConfigsTree()};
      const folder = getNodeByTreeAndPath(copyTree, folderPath);
      if (isEditing) {
        folder[keyName][K_INFO].titleEdit = true; // To visualize Meta Flags
      }
      else {
        delete folder[keyName][K_INFO].titleEdit;
      }

      updateConfigsRefSafely(copyTree)
        .then(()=> resolve('titleEdit'));
    });
  }, [getConfigsTree, updateConfigsRefSafely]);


  const setKeyEditing = useCallback(async (folderPath, keyName, isEditing = true) => {
    return new Promise((resolve, reject) => {
      console.log('[ConfigPathContextProvider setKeyEditing] [folderPath: %s] [keyName: %s] [isEditing: %s]',folderPath, keyName, isEditing);
      const copyTree = {...getConfigsTree()};
      const copyFolder = getNodeByTreeAndPath(copyTree, folderPath);
      if (isEditing) {
        copyFolder[keyName][K_INFO].keyEdit = true; // To visualize Meta Flags
      }
      else {
        delete copyFolder[keyName][K_INFO].keyEdit;
      }
      updateConfigsRefSafely(copyTree)
        .then(()=> resolve('keyEdit'));
    });
  }, [getConfigsTree, updateConfigsRefSafely]);
  
  //-------[   Set Value Locked   ]--------
  //
  const setValueLocked = useCallback( async (folderPath, keyName, isLocked = true) => {
    return new Promise((resolve, reject) => {
      console.log('[ConfigsDataContextProvider setValueLocked] [folderPath: %s] [keyName: %s] [isLocked: %s]', folderPath, keyName, isLocked);
      const copyTree = {...getConfigsTree()};
      const copyFolder = getNodeByTreeAndPath(copyTree, folderPath);
      if (isLocked) {
        copyFolder[keyName][K_INFO].valLocked = true; // valEdit, valSuccess
        delete copyFolder[keyName][K_INFO].valEdit;
      }
      else {
        delete copyFolder[keyName][K_INFO].valLocked;
      }
      updateConfigsRefSafely(copyTree)
        .then(()=>resolve('valLocked'));
    });
  }, [getConfigsTree, updateConfigsRefSafely]);

  const setTitleLocked = useCallback(async (folderPath, keyName, isLocked = true) => {
    return new Promise((resolve, reject) => {
      console.log('[ConfigsDataContextProvider setTitleLocked] %s / %s = %s', folderPath, keyName, isLocked);
      const copyTree = {...getConfigsTree()};
      const folder = getNodeByTreeAndPath(copyTree, folderPath);
      if (isLocked) {
        folder[keyName][K_INFO].titleLocked = true;
        delete folder[keyName][K_INFO].titleEdit;
      }
      else {
        delete folder[keyName][K_INFO].titleLocked;
      }

      updateConfigsRefSafely(copyTree)
        .then(()=>resolve('titleLocked'));
    });
  }, [getConfigsTree, updateConfigsRefSafely]);

  const setKeyLocked = useCallback(async (folderPath, keyName, isLocked = true) => {
    return new Promise((resolve, reject) => {
      console.log('[ConfigsDataContextProvider setKeyLocked] [folderPath: %s] [keyName: %s] [isLocked: %s]', folderPath, keyName, isLocked);
      const copyTree = {...getConfigsTree()};
      const copyFolder = getNodeByTreeAndPath(copyTree, folderPath);
      if (isLocked) {
        copyFolder[keyName][K_INFO].keyLocked = true; // keyEdit, keySuccess?
        delete copyFolder[keyName][K_INFO].keyEdit;
      }
      else {
        delete copyFolder[keyName][K_INFO].keyLocked;
      }
      updateConfigsRefSafely(copyTree)
        .then(()=>resolve('keyLocked'));
    });
  },[getConfigsTree, updateConfigsRefSafely]);

  const tagValueError = useCallback(async (valUpdatedError) => {
    return new Promise((resolve, reject) => {
      const {basePath:folderPath, newKey:keyName} = valUpdatedError; // TODO: Consider checking the `errorTag` === 'prop-val'...
      const copyTree = {...getConfigsTree()};
      const folder = getNodeByTreeAndPath(copyTree, folderPath);
      folder[keyName][K_INFO].valErrorInfo = valUpdatedError; // To display the Error Message.
      updateConfigsRefSafely(copyTree).then(()=>resolve('valError'));
    });
  },[getConfigsTree, updateConfigsRefSafely]);


  const tagTitleError = useCallback(async (titleUpdatedError) => {
    return new Promise((resolve, reject) => {
      const {basePath:folderPath, newKey:keyName} = titleUpdatedError; // TODO: Consider checking the `errorTag` === 'prop-val'...
      const copyTree = {...getConfigsTree()};
      const folder = getNodeByTreeAndPath(copyTree, folderPath);
      folder[keyName][K_INFO].titleErrorInfo = titleUpdatedError; // To display the Error Message.
      updateConfigsRefSafely(copyTree).then(()=>resolve('titleError'));
    });
  },[getConfigsTree, updateConfigsRefSafely]);


  const deleteConfigKey = useCallback( async (configKeyDeleted) => {
    return new Promise((resolve, reject) => {
      const {basePath:folderPath, key:keyName} = configKeyDeleted;
      const copyTree = {...getConfigsTree()};
      const folder = getNodeByTreeAndPath(copyTree, folderPath);
      if (folder) {
        delete folder[keyName];
      }
      else {
        console.warn('[ConfigsDataContextProvider deleteConfigKey] Ignored. Folder not exists: %s (folder just deleted before its item?) [keyName: %s]', folderPath, keyName);
      }
      updateConfigsRefSafely(copyTree)
        .then(()=>resolve('keyDeleted'));
    });
  }, [getConfigsTree, updateConfigsRefSafely]);

  const tagKeyDeletedError = useCallback(async (keyDeletedError) => {
    return new Promise((resolve, reject) => {
      const {basePath:folderPath, key:keyName} = keyDeletedError;
      const copyTree = {...getConfigsTree()};
      const folder = getNodeByTreeAndPath(folderPath, keyName);
      folder[keyName][K_INFO].keyDeletedErrorInfo = keyDeletedError;
      updateConfigsRefSafely(copyTree)
        .then(() => resolve('taggedError'));
    });
  }, [getConfigsTree, updateConfigsRefSafely]);

  //------[  Reset KeyDeletedError  ]------
  // Might be called if some retry would be implemented... either manual or auto.
  // Or if we conclude that the delete could not be done and we abort it so resetting the error...
  const resetKeyDeletedError = useCallback(async (keyDeletedError) => {
    return new Promise((resolve, reject) => {
      const {basePath:folderPath, key:keyName} = keyDeletedError;
      const copyTree = {...getConfigsTree()};
      const folder = getNodeByTreeAndPath(folderPath, keyName);
      delete folder[keyName][K_INFO].keyDeletedErrorInfo;
      updateConfigsRefSafely(copyTree)
        .then(()=>resolve('resetDeleteError'));
    })
  },[getConfigsTree, updateConfigsRefSafely]);


  //-----[   Update Config Val   ]------
  //
  const updateConfigVal = useCallback( async (configValUpdated) => {
    return new Promise((resolve, reject) => {
      if (configValUpdated) {
        //console.log('[ConfigDataContextProvider updateConfigVal] [configValUpdated: %s]', JSON.stringify(configValUpdated, null, 2));
        const {basePath:folderPath, key:keyName, value, whoModified, whyModified, whenModified, action} = configValUpdated;
        if (folderPath) {
          const copyTree = {...getConfigsTree()};
          const folder = getNodeByTreeAndPath(copyTree, folderPath);
          if (Boolean(folder) && Boolean(folder[keyName])) {
            const backKInfo = {...folder[keyName][K_INFO]};
            const backNode  = {...folder[keyName]};

            // Init with Backup KInfo and Node
            folder[keyName] = {}; // Init new object with new ref
            folder[keyName] = backNode;
            folder[keyName][K_INFO] = {}; // new ref
            folder[keyName][K_INFO] = backKInfo;

            // Override with updated info
            folder[keyName][K_VALUE] = value;            
            folder[keyName][K_INFO].whenModified = whenModified;
            folder[keyName][K_INFO].whoModified = whoModified;
            folder[keyName][K_INFO].whyModified = whyModified;
            folder[keyName][K_INFO].valSuccess = true;

            // Remove edit mode related
            delete folder[keyName][K_INFO].valEdit;
            delete folder[keyName][K_INFO].valErrorInfo;

            updateConfigsRefSafely(copyTree).then(()=>resolve('ValUpdated'));
          }
          else {
            resolve('noop: Folder or KeyName not exists');
          }
        }
        else {
          resolve('noop: Path not provided');
        }
      }
      else {
        resolve('noop: No Data Provided');        
      }
    });    
  }, [getConfigsTree, updateConfigsRefSafely]);


  //------[  Save Item from SDK  ]------
  //
  // If oldKey exists --> set the value to the given key.
  // Otherwise, create a new key and set its value.
  //  
  const saveItem = useCallback( async (sdkItemSaved) => {
    return new Promise( (resolve, reject) => {
      //console.log('[ConfigDataContextProvider saveItem] [sdkItemSaved: %s]', JSON.stringify(sdkItemSaved, null, 2));
      const {basePath:folderPath, key:keyName, value, oldKey, kiponosInfo:kInfo} = sdkItemSaved;

      if (!keyName) {
        resolve('noop: empty new key');
      }
      else if (!folderPath) {
        resolve('noop: Empty Path');
      }
      else { // Create a new key if required, then update the value.

        // Prepare for both new key, update val, rename key
        const copyTree = {...getConfigsTree()};
        const copyFolder = getNodeByTreeAndPath(copyTree, folderPath);

        if (!Boolean(copyFolder)) {
          resolve('noop: no folder node');
        }
        else if (!Boolean(copyFolder[keyName])) { // New Key. One Shot - Create Key and Update Val 
          copyFolder[keyName] = {};
          copyFolder[keyName][K_VALUE] = value;
          copyFolder[keyName][K_INFO] = { ...kInfo, justCreated: true }
          updateConfigsRefSafely(copyTree)
            .then(() => setCreateKeyInProgress(folderPath, null, false))
            .then(() => {
              console.log('ConfigsDataContextProvider saveItem] [itemSaved: %s]', JSON.stringify(sdkItemSaved, null, 2));
            })
            .finally(() => resolve('ItemCreated'));
        }
        else {
          // Key Already Exists. Update Value only:
          if (oldKey && (oldKey !== keyName)) {
            // RENAME - Implement if SDK supports it OR other reason.
            console.error('[ConfigsDataContextProvider saveItem] RENAME detected but not supported for this API');
            resolve('noop: Rename Not Supported');
          }
          else {
            // KeyName already exists, No oldKey so this is only a value update
            updateConfigVal({ basePath: folderPath, key: keyName, value, whoModified: kInfo.whoModified, whyModified: kInfo.whyModified, whenModified: kInfo.whenModified })
              .then(() => console.log('Full Item Saved [key: %s] [value: %s]', keyName, value))
              .finally(() => resolve('ItemSaved-Value-Updated'));
          }
        }
      }
    });
  }, [getConfigsTree, updateConfigVal, setCreateKeyInProgress, updateConfigsRefSafely]);


  // Update Insight 
  const updateInsight = useCallback(async (insightData) => {
    const doubleParsed = JSON.parse(insightData);
    const { basePath, improved } = doubleParsed;
    const { newItems, betterValues, insights } = improved;

    console.log('[updateInsight] [basePath: %s]', basePath);
    console.log('[updateInsight] [newItems: %s]', JSON.stringify(newItems, null, 2));

    //--- Create New Items
    const createItems = async () => {
      for (const item of newItems) {
        // Insight newItem has: 
        //    key, 
        //    value, 
        //    note, 
        //    reason
        // 
        // kInfo SHOULD HAVE:
        // kInfo.insight {
        //    rename, value, note, reason, newItem=true/false
        // }        
        await createConfigItem({
          basePath: basePath,
          key: item.key,
          kiponosInfo: {
            insight: {
              newItem: true,
              note: item.note,
              value: item.value,
              reason: item.reason
            }
          }
        });

        await updateConfigVal({ basePath: basePath, key: item.key, value: item.value, whoModified: 'Kiponos AI', whyModified: 'Insight', whenModified: new Date().getTime() })
      }
      return 'Done';
    }


    //--- Save Insight List
    const saveInsightList = async () => {
      return new Promise((resolve, reject) => {
        const copyTree = { ...getConfigsTree() };
        const folder = getNodeByTreeAndPath(copyTree, basePath);
        folder[K_INFO].insightList = insights;
        updateConfigsRefSafely(copyTree)
          .then(() => resolve('insightList-Saved'));
      });
    }

    //--- Save Better Valus
    const saveBetterValues = async () => {
      return new Promise((resolve, reject) => {
        const copyTree = { ...getConfigsTree() };
        const folder = getNodeByTreeAndPath(copyTree, basePath);

        for (const better of betterValues) {
          if (folder[better.key]) {
            folder[better.key][K_INFO].insight = {};
            folder[better.key][K_INFO].insight.betterValue = better.value;
            folder[better.key][K_INFO].insight.betterRename = better.rename;
            folder[better.key][K_INFO].insight.reason = better.reason;
            folder[better.key][K_INFO].insight.betterNode = better.note;
          }
          else {
            console.error('Insight BetterValues - Suggested Key not found: %s', better.key);
          }
        }

        updateConfigsRefSafely(copyTree)
          .then(() => resolve('betterValues-Saved'));
      });
    }


    return new Promise((resolve, reject) => {
      if (!newItems && !betterValues && !insights) {
        resolve('noop: No new items');
      }
      else {
        createItems()
          .then(() => saveInsightList())
          .then(() => saveBetterValues())
          .then(() => resolve('Insight-Updated'))
        // TODO: correct to reject here?  .catch(reject)
      }
    });
  }, [createConfigItem, updateConfigVal, getConfigsTree, updateConfigsRefSafely]);


  //------[   Update Title   ]------
  //
  const updateTitle = useCallback(async (configTitleUpdated) =>{
    return new Promise((resolve, reject) => {
      if (configTitleUpdated) {        
        console.log('[ConfigsDataContextProvider updateTitle] [configTitleUpdated: %s]', JSON.stringify(configTitleUpdated, null, 2));
        const {basePath:folderPath, key:keyName, title, whenModified, whoModified, whyModified} = configTitleUpdated;
        const copyTree = {...getConfigsTree()};
        const folder = getNodeByTreeAndPath(copyTree, folderPath);
        if (Boolean(folder) && Boolean(folder[keyName])) {
          const bak = {...folder[keyName][K_INFO]};
          folder[keyName][K_INFO] = {};
          folder[keyName][K_INFO] = {...bak};
          folder[keyName][K_INFO].title = title;
          folder[keyName][K_INFO].whenModified = whenModified;
          folder[keyName][K_INFO].whoModified = whoModified;
          folder[keyName][K_INFO].whyModified = whyModified;
          folder[keyName][K_INFO].titleSuccess = true;

          delete folder[keyName][K_INFO].titleEdit;
          delete folder[keyName][K_INFO].titleErrorInfo;

          updateConfigsRefSafely(copyTree).then(()=>resolve('TitleUpdated'));
        }
        else {
          console.warn('[ConfigsDataContextProvider updateTitle] Folder or KeyName not found %s / %s', folderPath, keyName);
          resolve('noop: NoFolderOrKeyName')
        }
      }
      else {              
        resolve('noop: NoData');
      }
    });
  },[getConfigsTree, updateConfigsRefSafely]);


  //
  // 
  const clearValKInfo = useCallback( async (folderPath, keyName) => {
    return new Promise((resolve, reject) => {
      if (Boolean(folderPath) && Boolean(keyName)) {
        const copyTree = {...getConfigsTree()};
        const folder = getNodeByTreeAndPath(copyTree, folderPath);
                
        delete folder[keyName][K_INFO].valEdit;                
        delete folder[keyName][K_INFO].valErrorInfo;

        updateConfigsRefSafely(copyTree).then(()=>resolve('clearValKInfo'));
      }
      else {
        console.error('[ConfigsDataContextProvider clearValKInfo] Arg missing');
        resolve('noop: args missing');
      }
    });
  }, [getConfigsTree, updateConfigsRefSafely]);

  const clearTitleKInfo = useCallback( async (folderPath, keyName) => {
    return new Promise((resolve, reject) => {
      if (Boolean(folderPath) && Boolean(keyName)) {
        console.log('[ConfigsDataContextProvider clearTitleKInfo] %s /%s', folderPath, keyName);
        
        const copyTree = {...getConfigsTree()};
        const folder = getNodeByTreeAndPath(copyTree, folderPath);
                
        delete folder[keyName][K_INFO].titleEdit;
        delete folder[keyName][K_INFO].titleErrorInfo;

        updateConfigsRefSafely(copyTree).then(()=>resolve('clearTitleKInfo'));
      }
      else {
        console.error('[ConfigsDataContextProvider clearTitleKInfo] Arg missing');
        resolve('noop: args missing');
      }
    });
  }, [getConfigsTree, updateConfigsRefSafely]);



  const resetKeyKInfo = useCallback( async (folderPath, keyName) => {
    return new Promise((resolve, reject) => {
      if (Boolean(folderPath) && Boolean(keyName)) {
        const copyTree = {...getConfigsTree()};
        const folder = getNodeByTreeAndPath(copyTree, folderPath);
                
        delete folder[keyName][K_INFO].keyEdit;
        delete folder[keyName][K_INFO].keyRenamedErrorInfo;

        updateConfigsRefSafely(copyTree).then(()=>resolve('clearKeyKInfo'));
      }
      else {
        console.error('resetKeyKInfo: Arg missing');
        resolve('noop: args missing');
      }
    });
  }, [getConfigsTree, updateConfigsRefSafely]);

  const resetFolderKInfo = useCallback(async (basePath, folderName) => {
    console.log('[ConfigsDataContextProvider resetFolderKInfo] [basePath: %s] [folderName: %s]', basePath, folderName);
    
    return new Promise((resolve, reject) => {
      if (Boolean(basePath) && Boolean(folderName)) {
        const copyTree = {...getConfigsTree()};
        const parentFolder = getNodeByTreeAndPath(copyTree, basePath);
        delete parentFolder[folderName][K_INFO].folderRenamedErrorInfo;
        delete parentFolder[folderName][K_INFO].folderRenamedInProgress;
        delete parentFolder[folderName][K_INFO].folderEdit; // TODO: Do we have such field?
        delete parentFolder[folderName][K_INFO].isRenameMode;; // TODO: Do we have such field?

        updateConfigsRefSafely(copyTree)
          .then(()=>resolve('clearFolderKInfo'));
      }
      else {
        resolve('noop');
      }
    });
  }, [getConfigsTree, updateConfigsRefSafely]);


  // TODO:
  // const updateConfigNote = useCallback( async (configNoteUpdated) => {
  // }, []);  


  //
  // ---   Memoized VALUE   ---
  //
  // Prevent unnecessary ref changes if parent componenet rerender or other reasons.
  // Will change ref only if deps changes
  const memoizedValue = useMemo(() => ({    
    getNode,
    updateConfigsRefSafely,
    createFolder, renameFolder, deleteFolder, addBlankFolder, addSetupBlankFolder, setCreatingInProgress, deleteBlankFolder, setRenameMode,
    createConfigItem, setCreateKeyInProgress, tagKeyCreatedError, resetKeyCreatedError, tagKeyDeletedError, resetKeyDeletedError, tagKeyRenamedError,
    setKeyLocked,setKeyEditing,resetKeyKInfo, deleteConfigKey, renameConfigKey,
    updateConfigVal, saveItem, setValueEditing, clearValKInfo, setValueLocked, tagValueError, updateTitle,
    setTitleEditing, clearTitleKInfo, setTitleLocked, tagTitleError, updateInsight, reasoning, setReasoning, insightRejected, setInsightRejected,
    // TODO: updateConfigNote,
    clearJustUpdated,
    pageReady, setPageReady,
    configReloadMsg,
    setConfigReloadMsg,
    loadCycle,
    updateSdkData,
    tagFolderCreatedError, tagFolderRenamedError, resetFolderRenamedError,
    tagCreateFolderInProgress, resetFolderKInfo, setFolderEdit
  }), 
      [ pageReady, addBlankFolder, addSetupBlankFolder, deleteBlankFolder, setRenameMode, createFolder, renameFolder, deleteFolder, setCreatingInProgress,
        createConfigItem, setCreateKeyInProgress, resetKeyCreatedError, tagKeyCreatedError, 
        renameConfigKey, setKeyLocked, setKeyEditing,
        resetKeyKInfo, tagKeyRenamedError, deleteConfigKey, tagKeyDeletedError, resetKeyDeletedError,
        updateConfigVal, saveItem, setValueEditing, clearValKInfo,    setValueLocked, tagValueError, 
        updateTitle, setTitleEditing, clearTitleKInfo,  setTitleLocked, tagTitleError,
        configReloadMsg, loadCycle, getNode, updateConfigsRefSafely, updateInsight, reasoning, setReasoning, insightRejected, setInsightRejected,
        clearJustUpdated,
        updateSdkData,
        tagFolderCreatedError, tagFolderRenamedError, resetFolderRenamedError,
        tagCreateFolderInProgress, resetFolderKInfo, setFolderEdit
      ]
    );

  //
  // --- Return ---
  //
  return (<ConfigsDataContext.Provider value={memoizedValue}> {children} </ConfigsDataContext.Provider>)
}

export function useConfigsDataContext() {
  return useContext(ConfigsDataContext);
}